import React, { useState } from 'react';
import OwlCarousel from 'react-owl-carousel';
import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';
import ReactCardFlip from 'react-card-flip';
import type { Job } from '../../../shared/types/common';
import JobTags from './JobTags';

interface JobSpotlightProps {
  jobs: Job[];
}

interface JobComponentProps {
  job: Job;
}

interface JobInfoTagProps {
  className: string;
  text: string;
}

/**
 * JobSpotlight Component - Legacy Design Compatible Version
 * Displays featured jobs in a carousel format matching the original design
 */
export const JobSpotlight: React.FC<JobSpotlightProps> = ({ jobs }) => {
  return (
    <div className="container mt-4 mb-4 JobSpotlight px-0">
      <div className="row">
        {/* Covers everything below xl. Was d-md-none, which left 768-1199px
            with neither carousel rendered — the responsive items below only
            make sense if this one owns that range. */}
        <div className="d-xl-none">
          <OwlCarousel
            className='owl-theme'
            loop
            margin={0}
            nav
            items={1}
            autoplay={true}
            autoplayTimeout={2000}
            responsive={{
              0: { items: 1 },
              768: { items: 2 },
              992: { items: 3 }
            }}
          >
            {jobs.map(job => <JobCard job={job} key={job.id} />)}
          </OwlCarousel>
        </div>
        <div className="d-none d-xl-block">
          <OwlCarousel
            className='owl-theme'
            loop
            margin={0}
            nav
            items={4}
            autoplay={true}
            autoplayTimeout={2000}
          >
            {jobs.map(job => <JobCard job={job} key={job.id} />)}
          </OwlCarousel>
        </div>
      </div>
    </div>
  );
}

function JobCard({ job }: JobComponentProps) {
  const backgroundStyle = { backgroundImage: `url('${job.image}')` };

  const handleClick = () => {
    if (job.url) {
      window.open(job.url, "_blank");
    }
  };

  return (
    <div className="col-12 my-3 d-flex owl-job" style={{ paddingLeft: 10, paddingRight: 10 }}>
      <div
        className="spotlight-card"
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
        aria-label={`View job: ${job.title} at ${job.practice_name}`}
      >
        <div className="spotlight-card__title">
          <h4>{job.title}</h4>
        </div>

        <div className="spotlight-card__info">
          <JobInfoTag className="fa fa-briefcase" text={job.practice_name} />
          <JobInfoTag className="fa fa-map-marker" text={`${job.practice_city}, ${job.practice_state}`} />
          {job.compensation && (
            <JobInfoTag className="fa fa-money" text={job.compensation} />
          )}
        </div>

        <div className="spotlight-card__tags">
          <JobTags job={job} />
        </div>
      </div>
    </div>
  );
}

function FlippableJobCard({ job }: JobComponentProps) {
  const backgroundStyle = { backgroundImage: `url('${job.image}')` };
  const [isFlipped, setIsFlipped] = useState(false);

  const handleClick = () => {
    if (job.url) {
      window.open(job.url, "_blank");
    }
  };

  return (
    <div
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
    >
      <ReactCardFlip isFlipped={isFlipped} flipDirection="horizontal">
        {/* Front of card */}
        <div className="col-12 my-3 d-flex align-items-stretch owl-job" style={{ paddingLeft: 10, paddingRight: 10 }}>
          <div
            className="col-12 d-flex justify-content-between border-top border-start border-end shadow-sm bg-icon align-items-stretch cursor-pointer"
            style={backgroundStyle}
            onClick={handleClick}
          >
            <div className="py-2 pt-1 pb-3 bg-icon-inside d-flex align-items-stretch col-12">
              <div className="col-12 d-flex flex-column">
                <div className="job-list-content d-flex flex-column flex-fill">
                  <div className="d-flex flex-row align-items-center py-3">
                    <h4 className="ps-3 mb-0 mt-1">{job.title}</h4>
                  </div>
                  <div className="d-flex mb-2 mx-3 flex-wrap-off flex-column my-auto">
                    <JobInfoTag className="fa fa-briefcase" text={job.practice_name} />
                    <JobInfoTag className="fa fa-map-marker" text={`${job.practice_city}, ${job.practice_state}`} />
                    {job.compensation && (
                      <JobInfoTag className="fa fa-money" text={job.compensation} />
                    )}
                  </div>
                  <div className="mx-3">
                    <JobTags job={job} />
                  </div>
                </div>
              </div>
              <div className="clearfix"></div>
            </div>
          </div>
        </div>

        {/* Back of card */}
        <div className="col-12 my-3 d-flex align-items-stretch owl-job" style={{ paddingLeft: 10, paddingRight: 10 }}>
          <div
            className="col-12 d-flex justify-content-between border-top border-start border-end shadow-sm bg-icon align-items-stretch cursor-pointer"
            style={backgroundStyle}
            onClick={handleClick}
          >
            <div className="py-2 pt-1 pb-3 bg-icon-inside d-flex align-items-stretch col-12">
              <div className="col-12 d-flex flex-column">
                <div className="job-list-content d-flex flex-column flex-fill">
                  <div className="d-flex flex-row align-items-center py-3">
                    <h4 className="ps-3 mb-0 mt-1">{job.title}</h4>
                  </div>
                  <div className="mx-3">
                    <p className="text-white">Click to view details</p>
                    {job.description && (
                      <p className="text-white small">{job.description.substring(0, 100)}...</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="clearfix"></div>
            </div>
          </div>
        </div>
      </ReactCardFlip>
    </div>
  );
}

function JobInfoTag({ className, text }: JobInfoTagProps) {
  return (
    <span className="profile-attribute-bubble rounded px-2 py-1 me-1 my-1">
      <i className={className} aria-hidden="true"></i> {text}
    </span>
  );
}

// Export additional components for flexibility
export { JobCard, FlippableJobCard, JobInfoTag };
export default JobSpotlight;
